import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import Map, { Source, Layer, useMap, MapProvider, Popup, NavigationControl, GeoJSONSource } from 'react-map-gl'
import MapImage from './MapImage'
import useQueryString, { QueryStringResult } from "use-query-string";

import type { FeatureCollection } from 'geojson'
import Spiderfy from '@nazka/map-gl-js-spiderfy';

import 'mapbox-gl/dist/mapbox-gl.css'
import 'react-loading-skeleton/dist/skeleton.css'
import 'swiper/css';
import 'swiper/css/navigation';
import { HAS_POINT_COUNT, MAPBOX_TOKEN, MAP_BOUNDS, MARKER_LAYER_LAYOUT, MAX_SPIDERFIER_ZOOM, clusterCountLayerStyle, clusterLayerStyle, shadowLayerStyle, spiderifyPoints, unclusteredActiveLayerStyle, unclusteredLayerStyle } from './constants'

import './styles.scss'
import '@/scss/global.scss'
import Listing from './Listing'
import Tile from './Tile';
import Filter from './Filter';
import Skeleton from 'react-loading-skeleton';
import MobilePopout from './MobilePopout';

export interface IPropertyMapProps {
	segment?: string
	nodeId?: string
	title?: string
	search?: string
	p_type?: string
	prestigeOnly?: boolean
}

interface IPopupInfo {
	lat: number
	lng: number
	data: any
}

const fetcher = (...args: any) => {

	console.log(args[0])

	return fetch(args[0][0], {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(args[0][1]),
	}).then(res => res.json())
}

const autocompleteFetcher = (url) => fetch(url).then((res) => res.json());


function updateQuery(path: any) {
	window.history.pushState(null, document.title, path);
}

type IUseQueryString = [QueryStringResult[0], QueryStringResult[1]]

const PropertyMap = ({ segment, nodeId, title, prestigeOnly,p_type,search }: IPropertyMapProps) => {
	const [popupInfo, setPopupInfo] = useState<IPopupInfo|false>(null);
    const [mapObj, setMapObj] = useState<any|null>(null);
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const [showMobileMap, setShowMobileMap] = useState<boolean>(false);
    const [showReset, setShowReset] = useState<boolean>(false);
    const [savedProperies, setSavedProperies] = useState<string[]>([]);
    const spiderfyRef = useRef(null);

	const [cursor, setCursor] = useState<string>('auto');

	const [query, setQuery] = useQueryString(window.location, updateQuery) as IUseQueryString

	const { data } = useSWR([
		`/umbraco/api/PropertyApi/Search`,
		{
			// @ts-ignore
			SoldInLastTwelveMonthsOnly: JSON.parse(query?.showAllDates || 'true'),
			Bathrooms: query?.baths || null,
			BathroomsUseExact: query?.bathsexact || false,
			Bedrooms: query?.beds || null,
			BedroomsUseExact: query?.bedsexact || false,
			Parking: query?.cars || null,
			ParkingUseExact: query?.carsexact || false,
			MaxPrice: query?.maxPrice || null,
			// MaxPricePW: query?.maxPrice || null,
			MinPrice: query?.minPrice || null,
			// MinPricePW:  || null,
			prestigeOnly: prestigeOnly,
			segment: segment,
			agent: query?.agent || null,
			Type: query?.propertyType || null,
			P_Type: p_type,
			Search: query.search || "",
			NodeId: nodeId
		}
	], fetcher, {
		keepPreviousData: true
	});

	const { data: dataAutocomplete } = useSWR(query.search?.length >= 3 ? `/umbraco/api/PropertyApi/AutocompleteSearch?search=${query.search || ""}&nodeId=${nodeId}` : null, autocompleteFetcher);

	const geojson: FeatureCollection = useMemo(() => {
		return {
			type: 'FeatureCollection',
			features:
				data?.properties?.filter((pin) => {
					return pin?.latCoordinates !== "0"
				})?.map((item: any) => {
					return {
						type: 'Feature',
						geometry: { type: 'Point', coordinates: [parseFloat(item?.lonCoordinates), parseFloat(item?.latCoordinates)] },
						properties: {
							...item,
							type: 'property'
						},
					}
				})
		}
	},[ data ])

    const bounds = useMemo(() => {
        // calculate bounds based on the points
        if (!geojson?.features?.length) return null;

        const _bounds = geojson?.features?.reduce((acc: any, point: any) => {
			if (point.geometry.coordinates[0] == "0" || isNaN(point.geometry.coordinates[0]) || isNaN(point.geometry.coordinates[1])) return acc

            return [
                [Math.min(acc[0][0], point.geometry.coordinates[0]), Math.min(acc[0][1], point.geometry.coordinates[1])],
                [Math.max(acc[1][0], point.geometry.coordinates[0]), Math.max(acc[1][1], point.geometry.coordinates[1])],
            ];
        }
        , [[Infinity, Infinity], [-Infinity, -Infinity]]);

        return _bounds;
    }, [geojson])

	const resetBounds = (items: any) => {

		setShowReset(false);

		mapObj.fitBounds(items, {
			padding: 100
		});

	}

	const increaseZoom = () => {
		mapObj.zoomIn();
		setShowReset(true);
	}

	const decreaseZoom = () => {
		mapObj.zoomOut();
		setShowReset(true);
	}

	useEffect(() => {
		if(search){
			setQuery({search: search})
		}
	}, [search])

    useEffect(() => {
        if(bounds && mapObj) {

			resetBounds(bounds)

			// requestAnimationFrame(() => {
			// 	const getBoundsFromViewport = mapObj.getBounds();
			// 	mapObj.setMaxBounds(getBoundsFromViewport);
			// })
        }
    }, [bounds, mapObj])

    const handleMarkersClick = (_ev: any) => {

		// console.log(_ev)
		_ev.originalEvent.stopPropagation();
		if (!_ev.features?.[0]) {
			return
		}
		switch (_ev.features[0]?.layer?.id) {
			case "unclustered-point":
				// mapObj.setCenter([_ev.features[0].geometry.coordinates[0], _ev.features[0].geometry?.coordinates[1]])

				const features = _ev.features

				// @ts-ignore
				if (_ev.features[0].properties?.id !== (popupInfo?.data?.id || null)) {
					setPopupInfo(() => ({lat: features[0].geometry?.coordinates[1], lng: features[0].geometry.coordinates[0], data: features[0].properties}));
				}
				break;
			case "clusters":
				setPopupInfo(false)
				setShowReset(true)
				const mapboxSource = mapObj?.getSource('my-data') as GeoJSONSource;

				// mapObj.setCenter([_ev.features[0].geometry.coordinates[0], _ev.features[0].geometry?.coordinates[1]])
				mapboxSource.getClusterExpansionZoom(_ev.features[0].id, (err: any, zoom: any) => {
					if (err) { return; }

					mapObj.easeTo({
						center: _ev.lngLat,
						zoom,
						duration: 500
					});
				})
				break;
			default:
				setPopupInfo(false)
				break;
		}
    };

	useEffect(() => {
		if (data?.savedProperties) {
			setSavedProperies(data?.savedProperties?.split(',') || [])
		}
	}, [data])

	const handleZoomEnd = (_map) => {
		// console.log(_map)
        if (!spiderfyRef.current) {
            spiderfyRef.current = new Spiderfy(_map.target, {
                onLeafClick: (feature) => {
					setPopupInfo(() => ({lat: feature.geometry?.coordinates[1], lng: feature.geometry.coordinates[0], data: {
						...feature.properties,
						heroImageUrls: JSON.stringify(feature.properties.heroImageUrls)
					}}));
                },
                onLeafHover: (feature, ev) => {
                    ev.target.getCanvas().style.cursor = feature ? 'pointer' : '';
                },
                minZoomLevel: MAX_SPIDERFIER_ZOOM,
                zoomIncrement: 2,
                closeOnLeafClick: false,
                circleSpiralSwitchover: 500, // match with the old spiderfier implementation
                circleOptions: {
                    leavesSeparation: 100,
                    leavesOffset: [0, 0],
                },
                spiralOptions: {
                    legLengthStart: 100,
                    legLengthFactor: 10,
                    leavesSeparation: 150,
                    leavesOffset: [0, 0],
                },
                spiderLegsAreHidden: false,
                spiderLegsWidth: 2,
                spiderLegsColor: '#FA6469',
                spiderLeavesLayout: MARKER_LAYER_LAYOUT,
                spiderLeavesPaint: {
                    'icon-color': '#FA6469',
                },
                maxLeaves: 255,
                renderMethod: 'flat',
            }); // create a new spiderfy object
            spiderfyRef.current.applyTo('markers-layer-spiderfier'); // apply to a markers layer spiderfier
        }
    };


    const handleStyleLoad = (_map: any) => {
        setMapObj(_map?.target);
    };

	const closeFilter = () => {
		setShowFilter(false);
	}

	const openFilter = () => {
		setShowFilter(true);
	}

	const openMobileMap = () => {
		setShowMobileMap(true);
	}

	const onMouseEnter = useCallback(() => setCursor('pointer'), []);
	const onMouseLeave = useCallback(() => setCursor('auto'), []);

	const hasFilter = useMemo(() => {
		let filter = {...query}
		delete filter.page
		delete filter.sort

		return Object.values(filter).some((val) => !!val)
	}, [query])

	const numberOfFilters = useMemo(() => {
		let filter = {...query}
		delete filter.page
		delete filter.sort
		// delete filter.search
		return Object.values(filter).reduce((acc, val) => !!val ? acc + 1 : acc, 0)
	}, [query])

	const resetQuery = () => {
		setQuery({
			search: '',
			agent: '',
			page: 1,
			beds: '',
			bedsexact: null,
			baths: '',
			bathsexact: null,
			cars: '',
			carsexact: null,
			minPrice: '',
			maxPrice: '',
			propertyType: '',
			solddate: '',
			showAllDates: ''
		})
	}

	const toggleToSaved = (id: string) => {
		if (savedProperies?.find(val => val == `${id}`)) {
			setSavedProperies([...savedProperies.filter(val => val !== `${id}`)])
		} else {
			setSavedProperies([...savedProperies, `${id}`])
		}
	}

	const isActiveFeature = useMemo(() => ['in', 'id', popupInfo ? popupInfo?.data?.id : ''], [popupInfo]);
	// const isNotActiveFeature = useMemo(() => ['not', 'id', popupInfo ? popupInfo?.data?.id : ''], [popupInfo]);

	return (
		<div className='prop-search' >
			<div className="top-spacer">
				<div className="title-container">
					<div className="container">
						<h1 className="h2">{title || 'Property for Sale - Northern Beaches'}</h1>
					</div>
				</div>
				<div className="map-container">
					{data?.properties ?
						<div className="listing-total">
							{geojson?.features?.length} Listings
						</div>
					:
						<Skeleton width={82} height={30} containerClassName='listing-total-skeleton' />
					}
					{/* {showReset &&
						<div className="reset-zoom" onClick={() => {resetBounds(bounds)}}>
							<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path fillRule="evenodd" clipRule="evenodd" d="M8.76105 2.05984C7.4413 1.88955 6.10217 2.1579 4.94994 2.82356C4.33169 3.18072 3.78566 3.64164 3.33329 4.1819V3.33332C3.33329 2.96513 3.03482 2.66665 2.66663 2.66665C2.29844 2.66665 1.99996 2.96513 1.99996 3.33332V5.99999C1.99996 6.36818 2.29844 6.66665 2.66663 6.66665H2.98451C2.99465 6.66689 3.00477 6.66689 3.01487 6.66665H5.33329C5.70148 6.66665 5.99996 6.36818 5.99996 5.99999C5.99996 5.6318 5.70148 5.33332 5.33329 5.33332H4.12739C4.5186 4.78326 5.0254 4.31981 5.61692 3.97808C6.51591 3.45872 7.56073 3.24934 8.59043 3.38221C9.62012 3.51507 10.5776 3.9828 11.3153 4.71335C12.053 5.4439 12.5301 6.39674 12.673 7.42509C12.7237 7.78977 13.0604 8.04432 13.4251 7.99364C13.7897 7.94296 14.0443 7.60624 13.9936 7.24156C13.8104 5.92353 13.199 4.70229 12.2535 3.76595C11.308 2.82962 10.0808 2.23013 8.76105 2.05984ZM3.32695 8.57489C3.27627 8.21021 2.93955 7.95566 2.57486 8.00634C2.21018 8.05702 1.95562 8.39374 2.00631 8.75842C2.18947 10.0764 2.80091 11.2977 3.74644 12.234C4.69196 13.1704 5.91911 13.7698 7.23886 13.9401C8.55862 14.1104 9.89775 13.8421 11.05 13.1764C11.6682 12.8193 12.2143 12.3583 12.6666 11.8181V12.6667C12.6666 13.0348 12.9651 13.3333 13.3333 13.3333C13.7015 13.3333 14 13.0348 14 12.6667V9.99999C14 9.6318 13.7015 9.33332 13.3333 9.33332H13.0154C13.0053 9.33309 12.9951 9.33309 12.985 9.33332H10.6666C10.2984 9.33332 9.99996 9.6318 9.99996 9.99999C9.99996 10.3682 10.2984 10.6667 10.6666 10.6667H11.8725C11.4813 11.2167 10.9745 11.6802 10.383 12.0219C9.48401 12.5413 8.43919 12.7506 7.40949 12.6178C6.3798 12.4849 5.42235 12.0172 4.68463 11.2866C3.94692 10.5561 3.46986 9.60324 3.32695 8.57489Z" fill="#333333"/>
								</svg>

							Reset zoom
						</div>
					} */}
					<div className="zoom-btn zoom-plus" onClick={() => increaseZoom()}>
					</div>
					<div className="zoom-btn zoom-minus" onClick={() => decreaseZoom()}>
					</div>
					<MapProvider>
						<Map
							maxBounds={MAP_BOUNDS}
							id="propertyMap"
							initialViewState={{
								latitude: -33.764866900510626,
								longitude: 151.2708529819774,
								zoom: 11,
							}}
							style={{ width: '100%', height: '100%' }}
							mapStyle="mapbox://styles/mapbox/streets-v9"
							interactiveLayerIds={['markers-layer-spiderfier', 'unclustered-point', 'clusters']}
							mapboxAccessToken={MAPBOX_TOKEN}
							onClick={handleMarkersClick}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
							onStyleData={handleStyleLoad}
							onZoomEnd={handleZoomEnd}
							cursor={cursor}
							scrollZoom={true}
							onDragEnd={() => {setShowReset(true)}}
							// onMouseDown={() => {setPopupInfo(false)}}
							maxZoom={18}
						>
							<MapImage />
							<NavigationControl position="bottom-right" />
							<Source
								id="my-data"
								type="geojson"
								cluster={true}
								clusterMaxZoom={MAX_SPIDERFIER_ZOOM}
								clusterRadius={25}
								data={geojson}
							>
								{(popupInfo) && (
									<Popup onClose={(e) => {setPopupInfo(() => false)}} closeButton={false} key={popupInfo?.data?.id} className='location-modal' style={{maxWidth: 250}} latitude={popupInfo?.lat || 0} longitude={popupInfo?.lng || 0}>
										{popupInfo && <Tile data={popupInfo?.data} saved={savedProperies?.find(val => val == popupInfo?.data?.id)} toggleToSaved={toggleToSaved} isLoggedIn={data?.isLoggedIn} altStyle={true} />}
										<div className='popout-close' onClick={() => {setPopupInfo(false)}}>
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path fillRule="evenodd" clipRule="evenodd" d="M3.52861 3.52858C3.78896 3.26823 4.21107 3.26823 4.47141 3.52858L8.00001 7.05717L11.5286 3.52858C11.789 3.26823 12.2111 3.26823 12.4714 3.52858C12.7318 3.78893 12.7318 4.21104 12.4714 4.47138L8.94282 7.99998L12.4714 11.5286C12.7318 11.7889 12.7318 12.211 12.4714 12.4714C12.2111 12.7317 11.789 12.7317 11.5286 12.4714L8.00001 8.94279L4.47141 12.4714C4.21107 12.7317 3.78896 12.7317 3.52861 12.4714C3.26826 12.211 3.26826 11.7889 3.52861 11.5286L7.0572 7.99998L3.52861 4.47138C3.26826 4.21103 3.26826 3.78892 3.52861 3.52858Z" fill="white"/>
											</svg>
										</div>
									</Popup>
								)}
								{/* <Layer {...shadowLayerStyle} /> */}
								<Layer
									{...unclusteredLayerStyle}
									// filter={isNotActiveFeature}
								/>
								<Layer
									id="markers-layer-spiderfier"
									type="symbol"
									filter={HAS_POINT_COUNT}
									layout={MARKER_LAYER_LAYOUT}
									// onMouseEnter={handleMouseEnter}
									// onMouseLeave={handleMouseLeave}
									minzoom={MAX_SPIDERFIER_ZOOM}
								/>
								{/*
								// @ts-ignore */}
								<Layer {...unclusteredActiveLayerStyle} filter={isActiveFeature} />
								<Layer
									{...clusterLayerStyle}
									maxzoom={MAX_SPIDERFIER_ZOOM}
								/>
								<Layer {...clusterCountLayerStyle} />
							</Source>
						</Map>
					</MapProvider>
				</div>
			</div>
			<Filter
				segment={segment}
				resetQuery={resetQuery}
				hasFilters={hasFilter}
				close={closeFilter}
				show={showFilter}
				setQuery={setQuery}
				dataAutocomplete={dataAutocomplete}
				query={query}
			/>
			<MobilePopout
				numberOfFilters={numberOfFilters}
				bounds={bounds}
				data={geojson}
				isLoggedIn={data?.isLoggedIn || false}
				handleStyleLoad={handleStyleLoad}
				show={showMobileMap}
				openFilter={openFilter}
				close={() => {setShowMobileMap(false)}}
			/>
			<Listing
				numberOfFilters={numberOfFilters}
				resetQuery={resetQuery}
				hasFilters={hasFilter}
				data={data}
				savedProperies={savedProperies}
				toggleToSaved={toggleToSaved}
				dataAutocomplete={dataAutocomplete}
				query={query}
				setQuery={setQuery}
				openFilter={openFilter}
				openMobileMap={openMobileMap}
			/>
		</div>
	)
}

export default PropertyMap
